import { Button } from '@sunwebgroup/uds-ui/button'
import { Dialog } from '@sunwebgroup/uds-ui/dialog'
import { IndeterminateProgressCircle } from '@sunwebgroup/uds-ui/IndeterminateProgressCircle'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useAuthProtection } from '@/scopes/account/common/hooks'
import { MetaTitle } from '@/scopes/common/components/MetaTitle'
import { getContextItemId } from '@/scopes/common/domain/policies/contextItemId'
import { useConfiguration, useConfigurationClient } from '@/scopes/common/infrastructure/config/ConfigProvider'
import configRepository from '@/scopes/common/infrastructure/config/configRepository'
import { useLocation } from '@/scopes/common/infrastructure/dom/location'
import queryStringClient from '@/scopes/common/infrastructure/query-strings/queryStringClient'
import { createBookingDetailsActions } from '../domain/actions'
import { createBookingDetailsApiClient } from '../infrastructure/bookingDetailsApiClient'
import { BookingDetailsDependenciesProvider, useBookingDetailsActions } from '../infrastructure/dependencies/dependencies'
import { createBookingDetailsStore } from '../infrastructure/store'
import { useBookingDetailsStore } from '../infrastructure/useBookingDetailsStore'
import {
  AccommodationDetailsSection,
  AppPromotionSection,
  BookingDetailsLayout,
  BusDetailsSection,
  ContactSection,
  DocumentsSection,
  EditPreferencesSheet,
  ExcursionsSection,
  ExtrasSection,
  FlightDetailsSection,
  GroupDetailsSection,
  Hero,
  ManageBookingSection,
  ParticipantsSection,
  PaymentSection,
  PreferencesSection,
  RebookedMessage,
  TransfersSection,
} from './components'
import {
  getAppPromotionPlacement,
  getIsBookingDetailsNotFound,
  getIsBookingDetailsPageLoadError,
  getIsBookingDetailsPageLoadIdle,
  getIsBookingDetailsPageLoadPending,
  getIsBookingDetailsPageLoadSuccess,
} from './selectors'
import styles from './styles.module.css'

export function BookingDetailsContent() {
  const actions = useBookingDetailsActions()
  const intl = useIntl()
  const myPortalLink = useConfiguration('links.myPortal')
  const { isAuthenticated } = useAuthProtection()
  const isPageLoadIdle = useBookingDetailsStore(getIsBookingDetailsPageLoadIdle)
  const isPageLoadPending = useBookingDetailsStore(getIsBookingDetailsPageLoadPending)
  const isPageLoadError = useBookingDetailsStore(getIsBookingDetailsPageLoadError)
  const isBookingNotFound = useBookingDetailsStore(getIsBookingDetailsNotFound)
  const isPageLoadSuccess = useBookingDetailsStore(getIsBookingDetailsPageLoadSuccess)
  const shouldLoadPage = isAuthenticated && isPageLoadIdle
  const shouldShowPageLoader = !isAuthenticated || isPageLoadPending
  const rebookedBookingNumber = useBookingDetailsStore(state => state.rebookedBookingNumber)
  const appPromotionPlacement = useBookingDetailsStore(getAppPromotionPlacement)
  const isOnHolidays = appPromotionPlacement === 'on-holidays'
  const isUpcoming = appPromotionPlacement === 'upcoming'

  React.useEffect(() => {
    if (!shouldLoadPage)
      return

    void actions.pageLoaded()
  }, [actions, shouldLoadPage])

  if (shouldShowPageLoader)
    return <BookingDetailsLoader />

  if (isBookingNotFound) {
    return (
      <BookingDetailsLayout>
        <div className={styles.centerContainer}>
          <Dialog
            type="custom"
            icon="InformationInCircle"
            text={intl.formatMessage({ id: 'booking_details_not_found' })}
            supportingText={intl.formatMessage({ id: 'booking_details_not_found_supporting_text' })}
            actionsSlot={(
              <Button as="a" href={myPortalLink} className={styles.notFoundButton}>
                {intl.formatMessage({ id: 'booking_details_not_found_cta' })}
              </Button>
            )}
          />
        </div>
      </BookingDetailsLayout>
    )
  }

  if (isPageLoadError) {
    return (
      <BookingDetailsLayout>
        <div className={styles.centerContainer}>
          <Dialog
            type="alert"
            text={intl.formatMessage({ id: 'load_error' })}
            supportingText={intl.formatMessage({ id: 'load_error_supporting_text' })}
          />
        </div>
      </BookingDetailsLayout>
    )
  }

  if (rebookedBookingNumber) {
    return (
      <BookingDetailsLayout hero={<Hero />}>
        <div className={styles.centerContainer}>
          <RebookedMessage bookingNumber={rebookedBookingNumber} />
        </div>
      </BookingDetailsLayout>
    )
  }

  if (isPageLoadSuccess) {
    return (
      <BookingDetailsLayout hero={<Hero />}>
        <div className={styles.sectionsLayout}>
          <div className={styles.mainSections}>
            <ParticipantsSection />
            <FlightDetailsSection />
            <BusDetailsSection />
            <TransfersSection />
            <DocumentsSection />
            <ExcursionsSection />
            <PreferencesSection className={styles.mobileOnlySection} />
            {!isOnHolidays && <ContactSection className={styles.mobileOnlySection} />}
            {isUpcoming && <AppPromotionSection className={styles.mobileOnlySection} />}
            <ManageBookingSection />
          </div>
          <div className={styles.sidebarSections}>
            <PaymentSection />
            {isOnHolidays && (
              <>
                <ContactSection />
                <AppPromotionSection />
              </>
            )}
            <GroupDetailsSection />
            <AccommodationDetailsSection />
            <ExtrasSection />
            <PreferencesSection className={styles.desktopOnlySection} />
            {!isOnHolidays && (
              <>
                <ContactSection className={styles.desktopOnlySection} />
                {isUpcoming && <AppPromotionSection className={styles.desktopOnlySection} />}
              </>
            )}
          </div>
        </div>
        <EditPreferencesSheet />
      </BookingDetailsLayout>
    )
  }
}

function BookingDetailsLoader() {
  return (
    <BookingDetailsLayout>
      <div className={styles.centerContainer}>
        <IndeterminateProgressCircle size={32} />
      </div>
    </BookingDetailsLayout>
  )
}

const queryClient = new QueryClient()

export function BookingDetails() {
  const configurationClient = useConfigurationClient()
  const siteName = useConfiguration('siteName')
  const location = useLocation()
  const [store] = React.useState(() => createBookingDetailsStore())
  const actions = React.useMemo(
    () => createBookingDetailsActions({
      apiClient: createBookingDetailsApiClient({
        contextItemId: getContextItemId(siteName),
      }),
      store,
      queryStringClient,
      configurationClient,
      configurationRepository: configRepository,
      location,
    }),
    [siteName, configurationClient, location, store],
  )

  return (
    <BookingDetailsDependenciesProvider dependencies={{ actions, store }}>
      <QueryClientProvider client={queryClient}>
        <MetaTitle titleTranslationTag="booking_details_meta_title" />
        <BookingDetailsContent />
      </QueryClientProvider>
    </BookingDetailsDependenciesProvider>
  )
}
